// Pure derivation of the detailed capture-rule sections from a variant spec.
// No React, no DOM — runs identically in the browser and in the Node prerender,
// exactly like `deriveRules.ts` and `allOpenings.ts`.
//
// This is the deep dive behind the short "Captures" theme card on the variant
// page. The card is generated by `deriveRuleStatements`; this module reuses the
// SAME `rules.fact.*` keys as the lead sentence of each section so the two
// surfaces can never drift apart across seven languages. New `rules.capture.*`
// keys exist only for material that has no `fact` key at all.
//
// The precedence ladder mirrors the engine's filter chain one-to-one — see
// `getAllLegalMoves` in packages/core/src/moves/capture-moves.ts:
//   1. captureCompareStrategy  → max pieces ('none' = no ladder at all)
//   2. preferKingCaptureOnTie  → the CAPTURING piece must be a king
//   3. maxWithKings            → max number of kings captured
//   4. compareCaptureSequence  → the sequence meeting a king earliest
//   5. whatever survives       → free choice
// Deriving it from the same flags the engine reads means a variant can never
// display a level the engine does not apply.

import type { VariantSpec } from '@draughts/shared';

export type CaptureSectionId =
  | 'mandatory'
  | 'geometry'
  | 'menVsKing'
  | 'multi'
  | 'ladder'
  | 'removal'
  | 'promotion'
  | 'noHuffing';

export interface CaptureLadderStep {
  /** `rules.capture.level*` — the rule itself, one sentence. */
  key: string;
  /** `rules.capture.why*` — why the rule exists / what it decides. */
  whyKey: string;
  /** Example position id, looked up in `captureExamples.ts`. */
  exampleId: string;
  /** `rules.capture.ex*` — what the diagram shows. */
  exampleCaptionKey: string;
}

export interface CaptureSection {
  id: CaptureSectionId;
  /** `rules.capture.*Title` */
  titleKey: string;
  /** Existing `rules.fact.*` keys, reused verbatim as the section's lead. */
  factKeys: string[];
  /** `rules.capture.*` paragraphs — only where no `fact` key covers it. */
  bodyKeys: string[];
  /** Populated for the `ladder` section only. */
  ladder?: CaptureLadderStep[];
  /** Example position id, looked up in `captureExamples.ts`. */
  exampleId?: string;
  /** `rules.capture.ex*` — what the diagram shows. */
  exampleCaptionKey?: string;
}

/**
 * The precedence ladder, in the order the engine applies it.
 *
 * A variant with `captureCompareStrategy: 'none'` (english, russian, …) has no
 * ladder: capture is mandatory but any capture will do, so the only "level" is
 * the free choice itself.
 */
export function deriveCaptureLadder(spec: VariantSpec): CaptureLadderStep[] {
  const { rules } = spec;
  const steps: CaptureLadderStep[] = [];

  if (rules.captureCompareStrategy !== 'none') {
    if (rules.captureCompareStrategy === 'maxValue') {
      steps.push({
        key: 'rules.capture.levelMaxValue',
        whyKey: 'rules.capture.whyMaxValue',
        exampleId: 'ladderMaxPieces',
        exampleCaptionKey: 'rules.capture.exMaxValue',
      });
    } else {
      steps.push({
        key: 'rules.capture.levelMaxPieces',
        whyKey: 'rules.capture.whyMaxPieces',
        exampleId: 'ladderMaxPieces',
        exampleCaptionKey: 'rules.capture.exMaxPieces',
      });
    }
  }

  if (rules.preferKingCaptureOnTie) {
    steps.push({
      key: 'rules.capture.levelWithKing',
      whyKey: 'rules.capture.whyWithKing',
      exampleId: 'ladderWithKing',
      exampleCaptionKey: 'rules.capture.exWithKing',
    });
  }

  if (rules.captureCompareStrategy === 'maxWithKings') {
    steps.push({
      key: 'rules.capture.levelMaxKings',
      whyKey: 'rules.capture.whyMaxKings',
      exampleId: 'ladderMaxKings',
      exampleCaptionKey: 'rules.capture.exMaxKings',
    });
  }

  if (rules.compareCaptureSequence) {
    steps.push({
      key: 'rules.capture.levelFirstKing',
      whyKey: 'rules.capture.whyFirstKing',
      exampleId: 'ladderFirstKing',
      exampleCaptionKey: 'rules.capture.exFirstKing',
    });
  }

  // Always last: past this point nothing constrains the player. Stating it is
  // the difference between "the ladder always resolves" (false) and the truth.
  // With no ladder at all the free choice IS the rule, and the example shows
  // something quite different — a short and a long capture, both legal.
  steps.push({
    key: 'rules.capture.levelFree',
    whyKey: 'rules.capture.whyFree',
    exampleId: 'ladderFree',
    exampleCaptionKey:
      rules.captureCompareStrategy === 'none'
        ? 'rules.capture.exFreeNoLadder'
        : 'rules.capture.exFree',
  });

  return steps;
}

/** Ordered sections of the detailed capture-rules page for a variant. */
export function deriveCaptureSections(spec: VariantSpec): CaptureSection[] {
  const { rules } = spec;
  const out: CaptureSection[] = [];

  // 1. Capture is mandatory. Enforced by exclusion in getAllLegalMoves: if any
  // piece can capture, every quiet move disappears from the legal set.
  out.push({
    id: 'mandatory',
    titleKey: 'rules.capture.mandatoryTitle',
    factKeys: [
      rules.mustCaptureMaximum ? 'rules.fact.maxCapturePieces' : 'rules.fact.captureMandatoryFree',
    ],
    bodyKeys: ['rules.capture.mandatoryBody', 'rules.capture.mandatoryRejected'],
  });

  // 2. Geometry: direction, distance, landing square.
  out.push({
    id: 'geometry',
    titleKey: 'rules.capture.geometryTitle',
    factKeys: [
      rules.captureType === 'mixed'
        ? 'rules.fact.captureMixed'
        : rules.captureType === 'orthogonal'
        ? 'rules.fact.captureOrthogonal'
        : 'rules.fact.captureDiagonal',
      rules.menCaptureBackward ? 'rules.fact.menCaptureBackwardYes' : 'rules.fact.menCaptureBackwardNo',
      rules.flyingKings ? 'rules.fact.kingFlying' : 'rules.fact.kingShort',
    ],
    bodyKeys: rules.flyingKings
      ? ['rules.capture.geometryLanding']
      : ['rules.capture.geometryShortKing', 'rules.capture.geometryLanding'],
    exampleId: 'geometry',
    exampleCaptionKey: 'rules.capture.exGeometry',
  });

  // 3. Men versus kings — the signature Italian-family restriction, and its
  // non-obvious consequence: an enemy king is a wall a man can never get past.
  out.push({
    id: 'menVsKing',
    titleKey: 'rules.capture.menVsKingTitle',
    factKeys: [rules.menCanCaptureKing ? 'rules.fact.menCaptureKingYes' : 'rules.fact.menCaptureKingNo'],
    bodyKeys: rules.menCanCaptureKing ? ['rules.capture.menVsKingFree'] : ['rules.capture.menVsKingWall'],
    exampleId: 'menVsKing',
    exampleCaptionKey: rules.menCanCaptureKing
      ? 'rules.capture.exMenVsKingFree'
      : 'rules.capture.exMenVsKingWall',
  });

  // 4. A multi-capture must be played to the end. True for EVERY variant here,
  // including the ones with no ladder: the generator only emits a sequence when
  // no continuation exists, so a truncated capture is not a legal move at all.
  out.push({
    id: 'multi',
    titleKey: 'rules.capture.multiTitle',
    factKeys: [],
    bodyKeys: [
      'rules.capture.multiBody',
      'rules.capture.multiDirection',
      'rules.capture.multiNoTwice',
    ],
    exampleId: 'multi',
    exampleCaptionKey: 'rules.capture.exMulti',
  });

  // 5. The precedence ladder.
  out.push({
    id: 'ladder',
    titleKey: 'rules.capture.ladderTitle',
    factKeys: [],
    bodyKeys:
      rules.captureCompareStrategy === 'none'
        ? ['rules.capture.ladderNoneIntro']
        : ['rules.capture.ladderIntro', 'rules.capture.ladderWholePosition'],
    ladder: deriveCaptureLadder(spec),
  });

  // 6. When the captured pieces leave the board. Only the exception is stated
  // as a fact key (mirroring deriveRules); the default gets its own prose.
  out.push({
    id: 'removal',
    titleKey: 'rules.capture.removalTitle',
    factKeys: rules.removesCapturedImmediately ? ['rules.fact.captureImmediateRemoval'] : [],
    bodyKeys: rules.removesCapturedImmediately
      ? ['rules.capture.removalImmediate']
      : ['rules.capture.removalEnd'],
  });

  // 7. Promotion during a capture, and its interaction with the maximum rule.
  out.push({
    id: 'promotion',
    titleKey: 'rules.capture.promotionTitle',
    factKeys: [
      rules.promotionStopsCapture
        ? 'rules.fact.promotionStops'
        : rules.promoteInPassing
        ? 'rules.fact.promotionInPassing'
        : 'rules.fact.promotionContinues',
    ],
    bodyKeys: rules.promotionStopsCapture
      ? rules.captureCompareStrategy === 'none'
        ? ['rules.capture.promotionBody']
        : ['rules.capture.promotionBody', 'rules.capture.promotionNoExemption', 'rules.capture.promotionStillAMan']
      : [],
    exampleId: rules.promotionStopsCapture ? 'promotion' : undefined,
    exampleCaptionKey: rules.promotionStopsCapture ? 'rules.capture.exPromotion' : undefined,
  });

  // 8. No huffing. Worth saying out loud precisely because english draughts is
  // the variant people remember the rule from.
  if (!rules.huffing) {
    out.push({
      id: 'noHuffing',
      titleKey: 'rules.capture.noHuffingTitle',
      factKeys: [],
      bodyKeys: ['rules.capture.noHuffingBody'],
    });
  }

  return out;
}
